import { adminApiConfig, getStoredAdminSession } from './admin-auth';
import type { PublicPortfolioItem } from './content-api';
import { optimizeImageForUpload } from './image-compression';

export type AdminPortfolioItem = PublicPortfolioItem & {
  created_at?: string;
  updated_at?: string;
};

type PortfolioDraft = Omit<AdminPortfolioItem, 'id'> & { id?: string };

function authHeaders(extra?: Record<string, string>) {
  const session = getStoredAdminSession();
  if (!session) throw new Error('Your admin session has expired. Sign in again.');
  return {
    apikey: adminApiConfig.publishableKey,
    Authorization: `Bearer ${session.access_token}`,
    ...extra,
  };
}

export async function listAdminPortfolio(): Promise<AdminPortfolioItem[]> {
  const response = await fetch(
    `${adminApiConfig.url}/rest/v1/portfolio_items?select=*&order=sort_order.asc`,
    { headers: authHeaders({ Accept: 'application/json' }) },
  );
  if (!response.ok) throw new Error('Unable to load portfolio content.');
  return response.json() as Promise<AdminPortfolioItem[]>;
}

export async function savePortfolioItem(item: PortfolioDraft): Promise<AdminPortfolioItem> {
  const { id, ...values } = item;
  const hasId = Boolean(id);
  const url = hasId
    ? `${adminApiConfig.url}/rest/v1/portfolio_items?id=eq.${encodeURIComponent(id!)}`
    : `${adminApiConfig.url}/rest/v1/portfolio_items`;
  const response = await fetch(url, {
    method: hasId ? 'PATCH' : 'POST',
    headers: authHeaders({
      'Content-Type': 'application/json',
      Prefer: 'return=representation',
    }),
    body: JSON.stringify(values),
  });
  const payload = await response.json().catch(() => []);
  if (!response.ok) {
    const message = Array.isArray(payload) ? undefined : payload?.message;
    throw new Error(message || 'Unable to save portfolio content.');
  }
  return payload[0] as AdminPortfolioItem;
}

export async function deletePortfolioItem(item: AdminPortfolioItem): Promise<void> {
  if (item.image_path) {
    const storageResponse = await fetch(
      `${adminApiConfig.url}/storage/v1/object/portfolio-images`,
      {
        method: 'DELETE',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ prefixes: [item.image_path] }),
      },
    );
    const deletedObjects = await storageResponse.json().catch(() => []);
    if (!storageResponse.ok || !Array.isArray(deletedObjects) || deletedObjects.length === 0) {
      throw new Error('Unable to delete the portfolio image.');
    }
  }

  const response = await fetch(
    `${adminApiConfig.url}/rest/v1/portfolio_items?id=eq.${encodeURIComponent(item.id)}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  if (!response.ok) throw new Error('Unable to delete portfolio content.');
}

export async function uploadPortfolioImage(file: File): Promise<{ path: string; url: string }> {
  const optimizedFile = await optimizeImageForUpload(file);
  const extension = optimizedFile.name.split('.').pop()?.toLowerCase() || 'jpg';
  const safeExtension = ['jpg', 'jpeg', 'png', 'webp'].includes(extension) ? extension : 'jpg';
  const path = `${Date.now()}-${crypto.randomUUID()}.${safeExtension}`;
  const response = await fetch(
    `${adminApiConfig.url}/storage/v1/object/portfolio-images/${path}`,
    {
      method: 'POST',
      headers: authHeaders({
        'Content-Type': optimizedFile.type || 'image/jpeg',
        'cache-control': '31536000',
        'x-upsert': 'false',
      }),
      body: optimizedFile,
    },
  );
  if (!response.ok) throw new Error('Image upload failed. Use JPG, PNG or WebP under 8 MB.');
  return {
    path,
    url: `${adminApiConfig.url}/storage/v1/object/public/portfolio-images/${path}`,
  };
}
